import {ChatMessage} from "../../types/chat-message";
import {Completer} from "../../../util/Completer";
import {ChatAttachment} from "../../types/chat-attachment";
import * as Sentry from "@sentry/react";

export enum ConnectionState {
  disconnected,
  connecting,
  active,
}
export class RocketChatSocketClient {
  private _socket: WebSocket | null = null;
  private _state: ConnectionState = ConnectionState.disconnected
  private _sessionId: string | null = null
  private _connectingCompleter: Completer<void> | null = null
  private _messageReceivedHandler: ((message: ChatMessage) => void) | null = null
  private _messageDeletedHandler: ((messageId: string) => void) | null = null
  private _pendingRequests: Map<string, Completer<any>> = new Map()

  constructor(
    private readonly hostname: string = 'chat.miep.online',
  ) {
  }

  getState(): ConnectionState {
    return this._state
  }

  connect(): Promise<void> {
    console.log('Connecting to RocketChat')
    if (this._socket) {
      console.warn('Closing existing connection in order to reconnect')
      this.disconnect()
    }
    this._connectingCompleter = new Completer()
    this._state = ConnectionState.connecting
    this._socket = new WebSocket(`wss://${this.hostname}/websocket`)
    this._socket.addEventListener('open', () => {
      console.log('Connected to RocketChat')
      this.send({msg: 'connect', version: '1', support: ['1']})
    })
    this._socket.addEventListener('close', async () => {
      console.log('Disconnected from RocketChat')
      this._socket = null
      this._state = ConnectionState.disconnected
      console.log('Reconnecting in 2 seconds...')
      await new Promise(resolve => setTimeout(resolve, 2000))
      this.connect()
    })
    this._socket.addEventListener('message', event => {
      try {
        this.handleMessage(JSON.parse(event.data))
      } catch (e) {
        console.error('Cannot parse incoming message', e)
      }
    })
    return this._connectingCompleter.promise
  }

  /**
   * Logs in using chat token
   */
  async login(chatToken: string): Promise<void> {
    await this.request<{id: string}>('login', [{ "resume": chatToken }])
  }

  async getRoomChanges(): Promise<{updated: {_id: string, t: string, ts: {$date: number}, name?: string, lastMessage?: {_id: string, rid: string, msg: string, u: {_id: string, username: string}, file?: {_id: string, type: string, name: string}, attachments?: ChatAttachment[], ts: {$date: number}}}[], removed: string[]}> {
    const response = await this.request<{update: {_id: string, t: string, ts: {$date: number}, name?: string}[]|null, remove: string[]|null}>("rooms/get", [{"$date": 0}])
    return {
      updated: response.update ?? [],
      removed: response.remove ?? [],
    }
  }

  async subscribeToMessages(): Promise<void> {
    this.send({
      msg: "sub",
      id: generateRandomId(),
      name: "stream-room-messages",
      params: ["__my_messages__", false]
    })
  }

  async subscribeToDeletedMessages(conversationId: string): Promise<void> {
    this.send({
      msg: "sub",
      id: `${generateRandomId()}-del`,
      name: "stream-notify-room",
      params: [`${conversationId}/deleteMessage`, false]
    })
  }

  async sendMessage(conversationId: string, chatMessage: string) {
    await this.request('sendMessage', [{
      rid: conversationId,
      msg: chatMessage,
      _id: generateRandomId() + "01",
    }])
  }

  /** @deprecated Use REST api instead. */
  async deleteMessage(messageId: string) {
    await this.request('deleteMessage', [{_id: messageId}])
  }

  async loadHistory(conversationId: string, limit = 100, fromDate?: number, toDate?: number): Promise<ChatMessage[]> {
    const fromDateParam = fromDate ? {"$date": fromDate} : null
    const toDateParam = toDate ? {"$date": toDate} : {"$date": new Date().getTime()}
    const rawMessages = await this.request<{messages: any[]}>('loadHistory', [ conversationId, fromDateParam, limit, toDateParam])
    return rawMessages.messages
      .map((rawMessage) => {
        const {_id, msg, ts, t, u, rid, attachments, file} = rawMessage
        return {
          id: _id,
          conversationId: rid,
          userId: u.username,
          message: msg,
          type: t,
          attachments,
          file,
          timestamp: new Date(ts.$date),
          read: false,
        }
      })
      .filter(m => m.userId !== 'fourfusion')
  }

  disconnect(): void {
    if (this._socket) {
      console.warn('Closing connection to RocketChat')
      this._socket.close()
      this._socket = null
      this._state = ConnectionState.disconnected
    }
  }

  onMessageReceived(handler: (message: ChatMessage) => void): void {
    this._messageReceivedHandler = handler
  }

  onMessageDeleted(handler: (messageId: string) => void): void {
    this._messageDeletedHandler = handler
  }

  private send(message: object): void {
    if (!this._socket) {
      console.warn('Cannot send message, no open connection', message)
      return
    }
    this._socket.send(JSON.stringify(message))
  }

  private handleMessage(message: any): void {
    switch (message.msg) {
      case 'connected':
        this._sessionId = message.session
        this._state = ConnectionState.active
        this._connectingCompleter?.resolve()
        break
      case "ping":
        this.send({msg: "pong"})
        break
      case "result":
        this.handleRequestResponse(message)
        break;
      case "changed":
        switch (message.collection) {
          case "stream-room-messages":
            this.handleNewMessage(message.fields)
            break;
          case "stream-notify-room":
            this.handleRoomUpdate(message.fields)
            break;
          default:
            console.warn('Unknown change collection: ', message)
        }
        break;
      case "error":
        switch (message.reason) {
          case "Must connect first":
            this._sessionId = null
            this._state = ConnectionState.disconnected
            break;
          case "Already connected":
            break;
          default:
            console.error('Unknown error: ', message)
        }
        break;
    }
  }

  private handleNewMessage(messageUpdate: any): void {
    const [message] = messageUpdate.args
    if ('t' in message) {
      // Do not handle any new system announcements
      return
    }
    const {rid, msg, _id, ts, u, file, attachments} = message
    const messageObject = {
      id: _id,
      conversationId: rid,
      userId: u.username,
      message: msg,
      attachments,
      file,
      timestamp: new Date(ts.$date),
      read: false,
    }
    this._messageReceivedHandler?.(messageObject)
  }

  private handleRoomUpdate(messageUpdate: any): void {
    if (messageUpdate.eventName.match(/\/deleteMessage$/)) {
      const [message] = messageUpdate.args
      this._messageDeletedHandler?.(message._id)
    }
  }

  private handleRequestResponse(result: any): void {
    const {id, result: response, error} = result
    const completer = this._pendingRequests.get(id)
    if (completer) {
      if (response) {
        completer.resolve(response)
        this._pendingRequests.delete(id)
      } else if (error) {
        console.error(`Error in RocketChat (requestId=${id}) Response:`, error)
        Sentry.captureException(error);
        completer.reject(new RocketChatSocketError(error.error ?? 'unknown', error.reason ?? 'Onbekende fout', error));
        this._pendingRequests.delete(id);
      }
    } else {
      console.warn(`Result for unknown request (id=${id}): `, result)
    }
  }

  /**
   * Send an asynchronous request
   */
  private request<T>(method: string, params: any): Promise<T> {
    const completer = new Completer<T>()
    const id = generateRandomId()
    this._pendingRequests.set(id, completer)

    this.send({
      msg: "method",
      id,
      method,
      params,
    })

    return completer.promise
  }

}

/**
 * Generate a time-based random numeric (string) ID
 */
let increment = 0
function generateRandomId(): string {
  increment++;
  return new Date().getTime().toString() + `${increment}`.padStart(8, '0')
}

export class RocketChatSocketError extends Error {
  constructor(public readonly errorSlug: string, public readonly reason: string, public readonly response: object) {
    super();
  }
}